import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2, Building2, User, Bell, CreditCard, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, ErrorBanner } from "../../components/app/ui";

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20";
const labelCls = "block text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400 mb-2";

function ComingSoonCard({ icon: Icon, title, sub, testId }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] p-7" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-center">
            <Icon className="w-[18px] h-[18px] text-slate-400" strokeWidth={1.7} />
          </span>
          <h3 className="font-display text-lg tracking-tight text-[#0F172A]">{title}</h3>
        </div>
        <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#0891B2] border border-[#0891B2]/25 rounded-full px-3 py-1">
          Coming soon
        </span>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed">{sub}</p>
      <div className="mt-5 flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50 px-4 py-3">
        <span className="text-xs text-slate-400">Enable {title.toLowerCase()}</span>
        <span className="w-9 h-5 rounded-full bg-slate-200 relative">
          <span className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow-sm" />
        </span>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { register, handleSubmit, reset } = useForm();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/clinic");
      reset({
        clinic_name: data?.clinic_name || "",
        email: data?.email || "",
        phone: data?.phone || "",
        address: data?.address || "",
      });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (values) => {
    setSaving(true);
    try {
      await api.put("/clinic", values);
      toast.success("Clinic profile saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader testId="settings-header" title="Settings" sub="Your clinic and account, with room to grow." />
      <ErrorBanner message={error} onRetry={load} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Clinic profile */}
        <section className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] p-7" data-testid="settings-clinic">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-xl bg-[#0891B2]/10 border border-[#0891B2]/15 flex items-center justify-center">
              <Building2 className="w-[18px] h-[18px] text-[#0891B2]" strokeWidth={1.7} />
            </span>
            <h3 className="font-display text-lg tracking-tight text-[#0F172A]">Clinic Profile</h3>
          </div>
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSave)} className="space-y-5" data-testid="clinic-form">
              <div>
                <label className={labelCls}>Clinic name</label>
                <input data-testid="clinic-name-input" className={inputCls} {...register("clinic_name", { required: true })} />
              </div>
              <div>
                <label className={labelCls}>Contact email</label>
                <input data-testid="clinic-email-input" type="email" className={inputCls} {...register("email")} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input data-testid="clinic-phone-input" className={inputCls} {...register("phone")} />
              </div>
              <div>
                <label className={labelCls}>Address</label>
                <input data-testid="clinic-address-input" className={inputCls} {...register("address")} />
              </div>
              <button
                type="submit"
                data-testid="clinic-save-button"
                disabled={saving}
                className="rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] px-7 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-shadow duration-300 hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)] disabled:opacity-60 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save changes
              </button>
            </form>
          )}
        </section>

        {/* User profile */}
        <section className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] p-7" data-testid="settings-profile">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-xl bg-[#2563EB]/10 border border-[#2563EB]/15 flex items-center justify-center">
              <User className="w-[18px] h-[18px] text-[#2563EB]" strokeWidth={1.7} />
            </span>
            <h3 className="font-display text-lg tracking-tight text-[#0F172A]">Your Profile</h3>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <span className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center text-lg font-bold text-white">
              {(user?.name || "U")[0].toUpperCase()}
            </span>
            <div>
              <p className="text-base font-medium text-[#0F172A]" data-testid="settings-user-name">{user?.name}</p>
              <p className="text-sm text-slate-400" data-testid="settings-user-email">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between rounded-xl border border-slate-200/80 bg-slate-50 px-4 py-3 text-sm">
              <span className="text-slate-400">Role</span>
              <span className="text-[#0F172A] capitalize" data-testid="settings-user-role">{user?.role || "user"}</span>
            </div>
            <div className="flex justify-between rounded-xl border border-slate-200/80 bg-slate-50 px-4 py-3 text-sm">
              <span className="text-slate-400">Sign-in method</span>
              <span className="text-[#0F172A] capitalize">{user?.provider || "email"}</span>
            </div>
          </div>
        </section>

        <ComingSoonCard
          testId="settings-notifications"
          icon={Bell}
          title="Notification Settings"
          sub="Choose how your clinic is alerted about new AI reviews, follow-ups and patient messages."
        />
        <ComingSoonCard
          testId="settings-billing"
          icon={CreditCard}
          title="Subscription & Billing"
          sub="Manage your Alignexa AI plan, seats and invoices — launching with paid plans."
        />
        <ComingSoonCard
          testId="settings-ai"
          icon={Sparkles}
          title="AI Preferences"
          sub="Tune how the AI copilot drafts notes and prioritizes your review queue. Always advisory — you decide."
        />
      </div>
    </div>
  );
}
